// WhatsApp API TypeScript classes matching backend C# classes

export class AccountInfo {
    id: string = '';
    name: string = '';
    phoneNumber?: string = '';

    constructor(accountInfo?: Partial<AccountInfo>) {
        Object.assign(this, accountInfo);
    }
}

export class GroupInfo {
    jid: string = '';
    subject: string = '';
    size: number = 0;

    constructor(groupInfo?: Partial<GroupInfo>) {
        Object.assign(this, groupInfo);
    }
}

export class CreateAccountRequest {
    accountId: string = '';

    constructor(request?: Partial<CreateAccountRequest>) {
        Object.assign(this, request);
    }
}

export class CreateAccountResponse {
    success: boolean = false;
    accountId: string = '';
    qr: string | null = null;
    status: string = 'disconnected';
    message: string | null = null;
    phoneNumber?: string = '';
    name?: string = '';

    constructor(response?: Partial<CreateAccountResponse>) {
        Object.assign(this, response);
    }
}

export class QRCodeResponse {
    qr: string | null = null;
    status: string = 'disconnected';
    hasQR: boolean = false;
    phoneNumber?: string = '';
    name?: string = '';

    constructor(response?: Partial<QRCodeResponse>) {
        Object.assign(this, response);
    }
}

export class AccountStatusResponse {
    accountId: string = '';
    status: string = 'disconnected';
    phoneNumber?: string = '';
    name?: string = '';

    constructor(response?: Partial<AccountStatusResponse>) {
        Object.assign(this, response);
    }
}

export class GroupRequest {
    accountId: string = '';
    groupJid: string = '';
    id: string | null = null;
    phoneNumber?: string = undefined;

    constructor(request?: Partial<GroupRequest>) {
        Object.assign(this, request);
    }
}

export enum MessageType {
    Regular = 'Regular',
    Delayed = 'Delayed',
    Scheduled = 'Scheduled'
}

export class AddToQueueRequest {
    groups: GroupRequest[] = [];
    message: string = '';
    imageUrl: string | null = null;
    documentUrl: string | null = null;
    configurationName: string | null = null;
    scheduledAt: string | null = null;
    skipPreview: boolean = false;
    messageType?: MessageType = undefined;
    delayHours?: number = undefined;
    delayMinutes?: number = undefined;
    delaySeconds?: number = undefined;

    constructor(request?: Partial<AddToQueueRequest>) {
        Object.assign(this, request);
    }
}

export class WhatsAppMessageDto {
    id: string = '';
    message: string = '';
    imageUrl: string | null = null;
    documentUrl: string | null = null;
    groupJid: string = '';
    accountId: string = '';
    status: string = 'pending';
    scheduledAt: string | null = null;
    createdAt: string = '';
    sentAt: string | null = null;
    errorMessage: string | null = null;
    configurationName: string | null = null;
    skipPreview: boolean = false;
    accountPhoneNumber?: string = undefined;
    messageType?: MessageType = undefined;
    whatsAppMessageId?: string | null = null;
    deliveredAt?: string | null = null;
    failedAt?: string | null = null;
    verificationAttempts: number = 0;

    constructor(item?: Partial<WhatsAppMessageDto>) {
        Object.assign(this, item);
    }
}

export class WhatsAppMessageItem {
    id: string = '';
    accountId: string = '';
    accountName: string = '';
    accountPhoneNumber?: string = undefined;
    groupJid: string = '';
    groupName: string = '';
    groupSubject: string = '';
    message: string = '';
    imageUrl: string | null = null;
    documentUrl: string | null = null;
    configurationName: string | null = null;
    status: string = '';
    scheduledAt?: string = undefined;
    scheduledAtLocal?: string = undefined;
    sentAt: string | null = null;
    deliveredAt?: string | null = undefined;
    failedAt?: string | null = undefined;
    errorMessage: string | null = null;
    whatsAppMessageId?: string | null = undefined;
    messageType?: MessageType = undefined;
    verificationAttempts?: number = 0;

    constructor(item?: Partial<WhatsAppMessageItem>) {
        Object.assign(this, item);
    }
}

export class QueueStatusResponse {
    [accountId: string]: WhatsAppMessageDto[];
}

export class EngineStatusResponse {
    running: boolean = false;
    queueProcessingEnabled: boolean = false;

    constructor(response?: Partial<EngineStatusResponse>) {
        Object.assign(this, response);
    }
}

export class StartEngineResponse {
    success: boolean = false;
    running: boolean = false;

    constructor(response?: Partial<StartEngineResponse>) {
        Object.assign(this, response);
    }
}

export class StopEngineResponse {
    success: boolean = false;
    running: boolean = false;

    constructor(response?: Partial<StopEngineResponse>) {
        Object.assign(this, response);
    }
}

export class QueueProcessingStatusResponse {
    enabled: boolean = false;

    constructor(response?: Partial<QueueProcessingStatusResponse>) {
        Object.assign(this, response);
    }
}

export class AccountWithStatusAndGroups {
    id: string = '';
    name: string = '';
    phoneNumber?: string = undefined;
    status: string = 'disconnected';
    groups: GroupInfo[] = [];

    constructor(account?: Partial<AccountWithStatusAndGroups>) {
        Object.assign(this, account);
        if (account?.groups) {
            this.groups = account.groups.map(g => new GroupInfo(g));
        }
    }
}

export class GetAllStatusResponse {
    engineStatus?: EngineStatusResponse = undefined;
    queueStatus?: QueueStatusResponse = undefined;
    queueProcessingStatus?: QueueProcessingStatusResponse = undefined;
    messages?: QueueStatusResponse = undefined;

    constructor(response?: Partial<GetAllStatusResponse>) {
        Object.assign(this, response);
        if (response?.engineStatus) {
            this.engineStatus = new EngineStatusResponse(response.engineStatus);
        }
        if (response?.queueProcessingStatus) {
            this.queueProcessingStatus = new QueueProcessingStatusResponse(response.queueProcessingStatus);
        }
    }
}

export class UploadFileResponse {
    url: string = '';

    constructor(response?: Partial<UploadFileResponse>) {
        Object.assign(this, response);
    }
}

export class MessageTemplate {
    id: string = '';
    name: string = '';
    content: string = '';
    useContentImage: boolean = false;

    constructor(template?: Partial<MessageTemplate>) {
        Object.assign(this, template);
        // Generate ID if not provided (for new templates)
        if (!this.id) {
            this.id = this.generateGuid();
        }
    }

    private generateGuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
}

export class WhatsAppConfiguration {
    id: string = '';
    name: string = '';
    groups: GroupInfo[] = [];
    defaultMessage: string = '';
    defaultTagLine: string = '';
    uniqueIdPrefix: string = '';
    messageTemplates: MessageTemplate[] = [];

    constructor(config?: Partial<WhatsAppConfiguration>) {
        Object.assign(this, config);
        if (config?.groups) {
            this.groups = config.groups.map(g => new GroupInfo(g));
        }
        if (config?.messageTemplates) {
            this.messageTemplates = config.messageTemplates.map(t => new MessageTemplate(t));
        }
        // Generate ID if not provided (for new configurations)
        if (!this.id) {
            this.id = this.generateGuid();
        }
        // Migrate defaultMessage to messageTemplates if templates array is empty
        if (this.messageTemplates.length === 0 && this.defaultMessage) {
            this.messageTemplates = [new MessageTemplate({ name: 'default', content: this.defaultMessage })];
        }
    }

    private generateGuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
}